import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Logs from "@/pages/Logs";
import Incidents from "@/pages/Incidents";
import IncidentDetail from "@/pages/IncidentDetail";
import Investigations from "@/pages/Investigations";
import InvestigationNew from "@/pages/InvestigationNew";
import InvestigationDetail from "@/pages/InvestigationDetail";
import ThreatIntel from "@/pages/ThreatIntel";
import Playbooks from "@/pages/Playbooks";
import Compliance from "@/pages/Compliance";
import Settings from "@/pages/Settings";
import Automation from "@/pages/Automation";
import Integrations from "@/pages/Integrations";
import AdminUsers from "@/pages/admin/Users";
import AdminApiKeys from "@/pages/admin/ApiKeys";
import Copilot from "@/pages/Copilot";
import EmailInvestigator from "@/pages/EmailInvestigator";
import ThreatMap from "@/pages/ThreatMap";
import ShiftBriefings from "@/pages/ShiftBriefings";
import AdminKioskTokens from "@/pages/admin/KioskTokens";
import AdminSlackSettings from "@/pages/admin/SlackSettings";
import AdminCustomerProfiles from "@/pages/admin/CustomerProfiles";
import KioskThreatMap from "@/pages/kiosk/KioskThreatMap";
import KioskBriefing from "@/pages/kiosk/KioskBriefing";
import PlatformTenants from "@/pages/platform/Tenants";
import PlatformSmtp from "@/pages/platform/Smtp";
import AdminIntelFeeds from "@/pages/admin/IntelFeeds";
import AdminConnectors from "@/pages/admin/Connectors";
import AskForensiQ from "@/pages/AskForensiQ";
import MfaSetup from "@/pages/MfaSetup";
import Marketing from "@/pages/Marketing";
import CVETracker from "@/pages/CVETracker";
import UserGuide from "@/pages/UserGuide";
import VerifySender from "@/pages/VerifySender";
import RootCauseAI from "@/pages/RootCauseAI";
import SidebarSettings from "@/pages/settings/SidebarSettings";

function Protected({ children }) {
  const { user } = useAuth();
  const { pathname } = window.location;
  if (!user) return <Navigate to="/login" replace />;
  // Super-admins (and any user with must_setup_mfa) are forced through MFA setup
  // before they can reach any other route.
  if (user.must_setup_mfa && !user.mfa_enabled && pathname !== "/mfa/setup") {
    return <Navigate to="/mfa/setup" replace />;
  }
  return <Layout>{children}</Layout>;
}

// Auth-required, but rendered WITHOUT the app sidebar/layout — used for the
// forced MFA setup screen so operators can't wander off before enrolling.
function MfaGate({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/mfa/setup" element={<MfaGate><MfaSetup /></MfaGate>} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/rca" element={<Protected><RootCauseAI /></Protected>} />
            <Route path="/settings/sidebar" element={<Protected><SidebarSettings /></Protected>} />
            <Route path="/investigations" element={<Protected><Investigations /></Protected>} />
            <Route path="/investigations/new" element={<Protected><InvestigationNew /></Protected>} />
            <Route path="/investigations/:id" element={<Protected><InvestigationDetail /></Protected>} />
            <Route path="/copilot" element={<Protected><Copilot /></Protected>} />
            <Route path="/email-investigator" element={<Protected><EmailInvestigator /></Protected>} />
            <Route path="/threat-map" element={<Protected><ThreatMap /></Protected>} />
            <Route path="/shift-briefings" element={<Protected><ShiftBriefings /></Protected>} />
            <Route path="/admin/kiosk" element={<Protected><AdminKioskTokens /></Protected>} />
            <Route path="/admin/slack" element={<Protected><AdminSlackSettings /></Protected>} />
            <Route path="/admin/customer-profiles" element={<Protected><AdminCustomerProfiles /></Protected>} />
            {/* Public kiosk viewer — no login required, token gates access via ?token= */}
            <Route path="/kiosk/threat-map" element={<KioskThreatMap />} />
            <Route path="/kiosk/briefing" element={<KioskBriefing />} />
            {/* Public sender-verification magic link — token IS the auth */}
            <Route path="/verify-sender" element={<VerifySender />} />
            <Route path="/logs" element={<Protected><Logs /></Protected>} />
            <Route path="/incidents" element={<Protected><Incidents /></Protected>} />
            <Route path="/incidents/:id" element={<Protected><IncidentDetail /></Protected>} />
            <Route path="/threat-intel" element={<Protected><ThreatIntel /></Protected>} />
            <Route path="/cve-tracker" element={<Protected><CVETracker /></Protected>} />
            {/* Public marketing/reading route — accessible without login */}
            <Route path="/guide" element={<UserGuide />} />
            <Route path="/quick-view" element={<Marketing />} />
            <Route path="/playbooks" element={<Protected><Playbooks /></Protected>} />
            <Route path="/compliance" element={<Protected><Compliance /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/automation" element={<Protected><Automation /></Protected>} />
            <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
            <Route path="/admin/users" element={<Protected><AdminUsers /></Protected>} />
            <Route path="/admin/apikeys" element={<Protected><AdminApiKeys /></Protected>} />
            <Route path="/admin/intel-feeds" element={<Protected><AdminIntelFeeds /></Protected>} />
            <Route path="/admin/connectors" element={<Protected><AdminConnectors /></Protected>} />
            <Route path="/ask" element={<Protected><AskForensiQ /></Protected>} />
            <Route path="/platform/tenants" element={<Protected><PlatformTenants /></Protected>} />
            <Route path="/platform/smtp" element={<Protected><PlatformSmtp /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right"
          toastOptions={{
            style: { background: "#121212", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", borderRadius: "2px", fontFamily: "IBM Plex Sans" },
          }}
        />
      </AuthProvider>
    </div>
  );
}
